import { DatePipe, DecimalPipe, isPlatformBrowser, NgClass, PercentPipe } from '@angular/common';
import {
  AfterViewInit,
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  PLATFORM_ID,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { NewLinePipe } from '@app/pipes/new-line.pipe';
import { RelativeTimePipe } from '@app/pipes/relative-time.pipe';
import { Instagram } from '@app/services/instagram.service';
import { DefaultLogoDirective } from '@directives/default-logo.directive';
import { UtilsService } from '@services/utils.service';

export interface TableColumn {
  def: string;
  title: string;
  type: ColumnType;
  list?: any[];
  sortDescOnly?: boolean;
  decimals?: number;
  subtitleDef?: string;
  subtitleFn?: (element: any) => string | null;
}

export enum ColumnType {
  Link = 'LINK',
  Embedded = 'EMBEDDED',
  List = 'LIST',
  Html = 'HTML',
  Text = 'TEXT',
  ArrayCount = 'ARRAYCOUNT',
  BreakText = 'BREAKTEXT',
  LongText = 'LONGTEXT',
  Date = 'DATE',
  Number = 'NUMBER',
  Boolean = 'BOOLEAN',
  Color = 'COLOR',
  Check = 'CHECK',
  Image = 'IMAGE',
  ImageStars = 'IMAGESTARS',
  Timings = 'TIMINGS',
  Dates = 'DATES',
  TitleLink = 'TITLELINK',
  Instagram = 'INSTAGRAM',
  Rating = 'RATING',
  Actions = 'ACTIONS',
  LinkActions = 'LINK_ACTIONS',
}

@Component({
  selector: 'app-table',
  imports: [
    NgClass,
    MatIconModule,
    MatTableModule,
    MatCheckboxModule,
    MatButtonModule,
    MatPaginatorModule,
    NewLinePipe,
    RelativeTimePipe,
    DefaultLogoDirective,
    DecimalPipe,
    DatePipe,
    PercentPipe,
  ],
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss']
})
export class TableComponent implements OnChanges, AfterViewInit {
  @Input() title = 'Tabela';
  @Input() extraButton = false;
  @Input({ required: true }) displayedColumns: string[] = [];
  @Input({ required: true }) items: any[] | null = [];
  @Input({ required: true }) columns: TableColumn[] = [];
  @Input() minitable: boolean = false;
  @Input() sortParam: string = '';
  @Input() storageKey: string = '';
  @Input() highlightTopValues = false;

  @Output() public editEmitter: EventEmitter<any> = new EventEmitter();
  @Output() public rightClickEmitter: EventEmitter<any> = new EventEmitter();
  @Output() public extraButtonEmitter: EventEmitter<any> = new EventEmitter();
  @Output() public deleteEmitter: EventEmitter<string> = new EventEmitter();
  @Output() public checkBoxEmitter: EventEmitter<any> = new EventEmitter();
  @Output() public cellClickEmitter: EventEmitter<string> = new EventEmitter();
  @Output() public sortChanged: EventEmitter<string> = new EventEmitter();

  @ViewChild(MatPaginator) paginator!: MatPaginator;

  public ColumnType = ColumnType;
  public dataSource = new MatTableDataSource<any>();
  public Math = Math;

  private platformId = inject(PLATFORM_ID);
  private initialLoad = false;
  private rankMap = new Map<string, any[]>(); // columnDef -> [#1, #2, #3] item refs

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['items']) {
      this.dataSource.data = this.items as any[];
      if (this.paginator && this.initialLoad) {
        this.dataSource.paginator = this.paginator;
        this.paginator.firstPage();
      }
      if (this.highlightTopValues) {
        this.buildRankMap();
      }
    }
  }

  private buildRankMap(): void {
    this.rankMap.clear();
    if (!this.items?.length) return;
    for (const col of this.columns) {
      if (col.type !== ColumnType.Number) continue;
      const sorted = [...this.items].sort((a: any, b: any) => (b[col.def] ?? 0) - (a[col.def] ?? 0));
      this.rankMap.set(col.def, sorted.slice(0, 3));
    }
  }

  getRank(element: any, columnDef: string): number {
    const top = this.rankMap.get(columnDef);
    if (!top) return 0;
    const idx = top.indexOf(element);
    return idx >= 0 ? idx + 1 : 0;
  }

  private applySortByParam(param: string): void {
    this.items!.sort((a: any, b: any) => (b[param] || 0) - (a[param] || 0));
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
    this.initialLoad = true;
    if (this.storageKey && isPlatformBrowser(this.platformId)) {
      this.paginator.page.subscribe(event => {
        localStorage.setItem(`admin_table_page_${this.storageKey}`, String(event.pageIndex));
      });
      this.restorePageFromStorage();
    }
  }

  private restorePageFromStorage(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const saved = localStorage.getItem(`admin_table_page_${this.storageKey}`);
    if (!saved) return;
    const pageIndex = parseInt(saved, 10);
    if (isNaN(pageIndex) || pageIndex <= 0) return;
    setTimeout(() => {
      const maxPage = this.paginator.getNumberOfPages() - 1;
      const targetPage = Math.min(pageIndex, maxPage >= 0 ? maxPage : 0);
      if (targetPage > 0) {
        this.paginator.pageIndex = targetPage;
        this.paginator.page.emit({
          previousPageIndex: 0,
          pageIndex: targetPage,
          pageSize: this.paginator.pageSize,
          length: this.paginator.length,
        });
      }
    });
  }

  async ngOnInit() {
    UtilsService.scrollTop();
  }

  getCurrentPageItems(): any[] {
    const { pageIndex, pageSize } = this.paginator ?? { pageIndex: 0, pageSize: 10 };
    const start = pageIndex * pageSize;
    return this.dataSource.data.slice(start, start + pageSize);
  }

  setCount(input: any) {
    if (!input) {
      return null;
    }

    const split = Array.isArray(input) ? input : input?.split('\n');
    return split?.length || 0;
  }

  private refreshDataSource(): void {
    this.dataSource.data = [...(this.items as any[])];
    if (this.paginator) {
      this.paginator.firstPage();
    }
  }

  onStarSort() {
    if (!this.items) {
      return;
    }

    this.items.sort((a: any, b: any) => {
      if (a.frontPage && !b.frontPage) {
        return -1;
      }
      if (!a.frontPage && b.frontPage) {
        return 1;
      }
      if (a.topTen && !b.topTen) {
        return -1;
      }
      if (!a.topTen && b.topTen) {
        return 1;
      }
      if (a.localHero && !b.localHero) {
        return -1;
      }
      if (!a.localHero && b.localHero) {
        return 1;
      }
      return -1;
    });

    this.refreshDataSource();
  }

  onSortCounter(columnDef: any, up: boolean) {
    if (!this.items) {
      return;
    }

    this.items.sort((a: any, b: any) => {
      const value1 = this.setCount(a[columnDef]) || null;
      const value2 = this.setCount(b[columnDef]) || null;
      if (up) {
        if (value1 && !value2) {
          return -1;
        }
        if (value2 && !value1) {
          return 1;
        }
        return value1 - value2;
      }

      return value2 - value1;
    });

    this.refreshDataSource();
  }

  onSortString(columnDef: any, up: boolean) {
    if (!this.items) {
      return;
    }

    this.items.sort((a: any, b: any) => {
      const value1 = a[columnDef] || '';
      const value2 = b[columnDef] || '';
      if (up) {
        if (value1 && !value2) {
          return -1;
        }
        if (value2 && !value1) {
          return 1;
        }
        return value1.localeCompare(value2);
      }

      return value2.localeCompare(value1);
    });

    this.refreshDataSource();
  }

  onSortNumber(columnDef: any, up: boolean) {
    if (!this.items) {
      return;
    }

    this.items.sort((a: any, b: any) => {
      const value1 = a[columnDef] || null;
      const value2 = b[columnDef] || null;
      if (up) {
        if (value1 && !value2) {
          return -1;
        }
        if (value2 && !value1) {
          return 1;
        }
        return value1 - value2;
      }

      return value2 - value1;
    });

    this.refreshDataSource();
  }

  onSortByParam(param1: any) {
    if (!this.items) {
      return;
    }

    this.applySortByParam(param1);
    this.sortChanged.emit(param1);
    this.refreshDataSource();
  }

  onSortByParams(param1: any, param2: any) {
    if (!this.items) {
      return;
    }

    this.items.sort((a: any, b: any) => {
      const value1 = (a[param1] || 0) / (a[param2] || 1);
      const value2 = (b[param1] || 0) / (b[param2] || 1);
      return value2 - value1;
    });

    this.refreshDataSource();
  }

  handleList(column: any, ids: string[] = []): string {
    if (column.list.length) {
      const displayList = column.list
        .filter((e: any) => ids.includes(e.id))
        .map((e: any) => e.name || e.id);
      return displayList.join(', ');
    }
    return '-';
  }


  onLink(event: any, post: Instagram) {
    event.stopPropagation();
    event.preventDefault();
    const dropPrefix = post.embeddedCode!.split('https')[1];
    const final = 'https' + dropPrefix!.split('?utm_source')[0];
    window.open(final!, '_blank');
  }

  olderThan2Months(element: any, column: any) {
    const todayTimestampMs = Date.now();
    const day = 86_400_000;
    const diff = todayTimestampMs - element[column.def];
    return diff >= 60 * day;
  }

  olderThan2Weeks(element: any, column: any) {
    const todayTimestampMs = Date.now();
    const day = 86_400_000;
    const diff = todayTimestampMs - element[column.def];
    return diff > 14 * day && diff < 60 * day;
  }

}
